// Admin billing actions client (Pass B / Module 47).
//
// Wraps the FastAPI proxy endpoints introduced in /api/admin/invoices/*
// and /api/admin/billing-config. Browser never holds service-role tokens —
// the proxy does it server-side.

import { supabase } from "@/lib/supabase";

const API_BASE = process.env.REACT_APP_BACKEND_URL || "";

async function authHeader() {
  const { data } = await supabase.auth.getSession();
  const token = data?.session?.access_token;
  if (!token) throw new Error("Sign in again");
  return { Authorization: `Bearer ${token}` };
}

export function invoicePdfUrl(invoiceId) {
  return `${API_BASE}/api/admin/invoices/${invoiceId}/pdf`;
}

/** Fetch a fresh blob URL for the invoice PDF — usable in <iframe> or download links. */
export async function fetchInvoicePdfBlob(invoiceId) {
  const headers = await authHeader();
  const res = await fetch(invoicePdfUrl(invoiceId), { headers });
  if (!res.ok) {
    const err = new Error(`PDF failed (${res.status})`);
    err.status = res.status;
    throw err;
  }
  const blob = await res.blob();
  return URL.createObjectURL(blob);
}

export async function emailInvoice({ invoiceId, to = null, note = null }) {
  const headers = {
    "Content-Type": "application/json",
    ...(await authHeader()),
  };
  const res = await fetch(`${API_BASE}/api/admin/invoices/${invoiceId}/email`, {
    method: "POST",
    headers,
    body: JSON.stringify({ to, note }),
  });
  if (!res.ok) {
    let detail = "";
    try {
      const raw = await res.text();
      try {
        detail = JSON.parse(raw).detail || raw;
      } catch {
        detail = raw;
      }
    } catch {
      detail = "";
    }
    const err = new Error(detail || `Email failed (${res.status})`);
    err.status = res.status;
    throw err;
  }
  return res.json();
}

/** Operational visibility — booleans + last webhook timestamp.
 *  No secrets ever leave the server. */
export async function fetchBillingConfig() {
  const headers = await authHeader();
  const res = await fetch(`${API_BASE}/api/admin/billing-config`, { headers });
  if (!res.ok) {
    const err = new Error(`Config fetch failed (${res.status})`);
    err.status = res.status;
    throw err;
  }
  return res.json();
}

/**
 * Fetch the organization-level HoneyAcorn billing contract. This is separate
 * from family invoice billing and intentionally exposes no Stripe secrets,
 * IDs, prices, or payment actions.
 */
export async function fetchProductBillingStatus() {
  const headers = await authHeader();
  const res = await fetch(`${API_BASE}/api/billing/status`, { headers });
  if (!res.ok) {
    const err = new Error(`Product billing status fetch failed (${res.status})`);
    err.status = res.status;
    throw err;
  }
  return res.json();
}
